import { getComponent } from "../data/catalog";
import { getRack } from "../data/maps";
import type {
    ComponentDefinition,
    FixedComponentDefinition,
    MapDefinition,
    Placement,
    RackDefinition,
    ValidationResult,
} from "./types";

const WALL_RACK_CATEGORIES: readonly string[] = Object.freeze(["Gateways", "Switches", "Security"]);
const WALL_RACK_BLOCKED_COMPONENT_IDS: readonly string[] = Object.freeze(["siem-2u"]);

export function createId(prefix: string): string {
    const randomId = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    return `${prefix}-${randomId}`;
}

export function createPlacement(
    component: ComponentDefinition,
    rackId: string,
    startU: number,
    localName?: string,
): Placement {
    const base = {
        id: createId("placement"),
        componentId: component.id,
        rackId,
        startU,
    };
    return localName === undefined || localName.trim() === "" ? base : { ...base, localName: localName.trim() };
}

export function getOccupiedRange(startU: number, sizeU: number): readonly number[] {
    if (!Number.isInteger(startU) || !Number.isInteger(sizeU) || startU < 1 || sizeU < 1 || sizeU > 42) {
        return [];
    }
    return Array.from({ length: sizeU }, (_, index) => startU + index);
}

function rangesOverlap(startA: number, sizeA: number, startB: number, sizeB: number): boolean {
    const endA = startA + sizeA - 1;
    const endB = startB + sizeB - 1;
    return startA <= endB && startB <= endA;
}

export function isWithinRack(rack: RackDefinition, startU: number, sizeU: number): boolean {
    return Number.isInteger(startU) && startU >= 1 && sizeU >= 1 && startU + sizeU - 1 <= rack.sizeU;
}

export function isComponentCompatibleWithRack(rack: RackDefinition, component: ComponentDefinition): boolean {
    if (rack.type !== "wall-4u") {
        return true;
    }
    return WALL_RACK_CATEGORIES.includes(component.category) && !WALL_RACK_BLOCKED_COMPONENT_IDS.includes(component.id);
}

export function collidesWithFixed(
    rack: RackDefinition,
    startU: number,
    sizeU: number,
): FixedComponentDefinition | undefined {
    return rack.fixedComponents?.find((fixed) => {
        const definition = getComponent(fixed.componentId);
        return definition !== undefined && rangesOverlap(startU, sizeU, fixed.startU, definition.sizeU);
    });
}

export function rackContentsFitSize(rack: RackDefinition, sizeU: number, placements: readonly Placement[]): boolean {
    if (!Number.isInteger(sizeU) || sizeU < 1) {
        return false;
    }
    for (const fixed of rack.fixedComponents ?? []) {
        const definition = getComponent(fixed.componentId);
        if (definition !== undefined && fixed.startU + definition.sizeU - 1 > sizeU) {
            return false;
        }
    }
    for (const placement of placements) {
        if (placement.rackId !== rack.id) {
            continue;
        }
        const definition = getComponent(placement.componentId);
        if (definition !== undefined && placement.startU + definition.sizeU - 1 > sizeU) {
            return false;
        }
    }
    return true;
}

export function validatePlacement(
    map: MapDefinition,
    placements: readonly Placement[],
    componentId: string,
    rackId: string,
    startU: number,
    ignoredPlacementId?: string,
): ValidationResult {
    const rack = getRack(map, rackId);
    const component = getComponent(componentId);
    if (rack === undefined) {
        return { valid: false, reason: "Unknown target rack." };
    }
    if (component === undefined) {
        return { valid: false, reason: "Unknown component type." };
    }
    if (component.userPlaceable === false) {
        return { valid: false, reason: "This component is reserved for fixed map equipment." };
    }
    if (!isComponentCompatibleWithRack(rack, component)) {
        return { valid: false, reason: "This component type is not supported by the target rack." };
    }
    if (!isWithinRack(rack, startU, component.sizeU)) {
        return { valid: false, reason: `Requires ${component.sizeU}U within the ${rack.sizeU}U rack boundary.` };
    }
    if (collidesWithFixed(rack, startU, component.sizeU) !== undefined) {
        return { valid: false, reason: "These rack units are reserved by fixed map equipment." };
    }

    const collision = placements.find((placement) => {
        if (placement.id === ignoredPlacementId || placement.rackId !== rackId) {
            return false;
        }
        const placedComponent = getComponent(placement.componentId);
        return (
            placedComponent !== undefined &&
            rangesOverlap(startU, component.sizeU, placement.startU, placedComponent.sizeU)
        );
    });
    if (collision !== undefined) {
        return { valid: false, reason: "These rack units overlap another component." };
    }
    return { valid: true };
}

export function getRackUsage(
    rack: RackDefinition,
    placements: readonly Placement[],
): { usedU: number; freeU: number; componentCount: number } {
    const occupied = new Set<number>();
    const rackPlacements = placements.filter((placement) => placement.rackId === rack.id);
    for (const placement of rackPlacements) {
        const component = getComponent(placement.componentId);
        if (component !== undefined) {
            for (const unit of getOccupiedRange(placement.startU, component.sizeU)) {
                occupied.add(unit);
            }
        }
    }
    for (const fixed of rack.fixedComponents ?? []) {
        const component = getComponent(fixed.componentId);
        if (component !== undefined) {
            for (const unit of getOccupiedRange(fixed.startU, component.sizeU)) {
                occupied.add(unit);
            }
        }
    }
    return {
        usedU: occupied.size,
        freeU: Math.max(0, rack.sizeU - occupied.size),
        componentCount: rackPlacements.length + (rack.fixedComponents?.length ?? 0),
    };
}

export function findFirstFreeStartU(
    map: MapDefinition,
    placements: readonly Placement[],
    componentId: string,
    rackId: string,
): number | null {
    const rack = getRack(map, rackId);
    const component = getComponent(componentId);
    if (rack === undefined || component === undefined) {
        return null;
    }
    const maximumStartU = rack.sizeU - component.sizeU + 1;
    for (let startU = 1; startU <= maximumStartU; startU += 1) {
        if (validatePlacement(map, placements, componentId, rackId, startU).valid) {
            return startU;
        }
    }
    return null;
}

export function findHighestFreeStartU(
    map: MapDefinition,
    placements: readonly Placement[],
    componentId: string,
    rackId: string,
): number | null {
    const rack = getRack(map, rackId);
    const component = getComponent(componentId);
    if (rack === undefined || component === undefined) {
        return null;
    }
    const maximumStartU = rack.sizeU - component.sizeU + 1;
    for (let startU = maximumStartU; startU >= 1; startU -= 1) {
        if (validatePlacement(map, placements, componentId, rackId, startU).valid) {
            return startU;
        }
    }
    return null;
}
